import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigation } from '@refinedev/core';
import { Create } from '@refinedev/antd';
import { useSearchParams } from 'react-router-dom';
import {
  Steps,
  Card,
  Table,
  Input,
  Row,
  Col,
  Tag,
  Button,
  Space,
  Descriptions,
  Statistic,
  Checkbox,
  Tooltip,
  message,
  Empty,
  Spin,
  DatePicker,
} from 'antd';
import {
  CarOutlined,
  SearchOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  IdcardOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiClient } from '../../lib/api-client';

// ============ TYPES ============

interface BusLayout {
  id: number;
  name: string;
  totalSeats: number;
  rows: number;
  leftSeats: number;
  rightSeats: number;
  backRowSeats: number;
  hasBackRow: boolean;
  seatConfig?: number[][];
}

interface BusTripOption {
  id: number;
  busCompany: { id: number; name: string };
  departureRegion: { nameUz: string };
  departureCity: { nameUz: string };
  arrivalRegion: { nameUz: string };
  arrivalCity: { nameUz: string };
  departureBusStation?: { nameUz: string } | null;
  arrivalBusStation?: { nameUz: string } | null;
  departureTime: string;
  arrivalTime: string;
  pricePerSeat: number;
  totalSeats: number;
  availableSeats: number;
  bookedSeats: number[];
  busType: string;
  busNumber: string | null;
  busLayout?: BusLayout | null;
  seatMap?: {
    total: number;
    booked: number[];
    available: number[];
  };
}

interface Passenger {
  name: string;
  phone: string;
  passport: string;
  birthDate: string;
  email: string;
  seatNumber: number;
}

// ============ INTERACTIVE SEAT MAP ============

const InteractiveSeatMap = ({
  totalSeats,
  rows,
  leftSeats,
  rightSeats,
  backRowSeats,
  hasBackRow,
  seatConfig,
  bookedSeats = [],
  selectedSeats = [],
  onSeatToggle,
}: {
  totalSeats: number;
  rows: number;
  leftSeats: number;
  rightSeats: number;
  backRowSeats: number;
  hasBackRow: boolean;
  seatConfig?: number[][];
  bookedSeats?: number[];
  selectedSeats?: number[];
  onSeatToggle: (seatNumber: number) => void;
}) => {
  const seatSize = 40;
  const gap = 8;
  const aisleWidth = 28;

  const layout = useMemo(() => {
    if (seatConfig && seatConfig.length > 0) {
      const sortedSeats = [...seatConfig].sort((a, b) => {
        if (a[0] !== b[0]) return a[0] - b[0];
        return a[1] - b[1];
      });
      return sortedSeats.map(([row, col], index) => ({
        number: index + 1,
        row,
        col,
        isWindow: col === 0 || col === 5,
      }));
    }

    const seats: { number: number; row: number; col: number; isWindow: boolean }[] = [];
    let seatNumber = 1;
    const seatsPerRow = leftSeats + rightSeats;
    const regularRows = hasBackRow
      ? Math.ceil((totalSeats - backRowSeats) / seatsPerRow)
      : Math.ceil(totalSeats / seatsPerRow);

    for (let row = 0; row < regularRows && seatNumber <= totalSeats; row++) {
      for (let col = 0; col < leftSeats && seatNumber <= totalSeats; col++) {
        if (!hasBackRow || seatNumber <= totalSeats - backRowSeats) {
          seats.push({ number: seatNumber++, row, col, isWindow: col === 0 });
        }
      }
      for (let col = 0; col < rightSeats && seatNumber <= totalSeats; col++) {
        if (!hasBackRow || seatNumber <= totalSeats - backRowSeats) {
          seats.push({ number: seatNumber++, row, col: leftSeats + col, isWindow: col === rightSeats - 1 });
        }
      }
    }
    if (hasBackRow) {
      const backRow = regularRows;
      for (let col = 0; col < backRowSeats && seatNumber <= totalSeats; col++) {
        seats.push({ number: seatNumber++, row: backRow, col, isWindow: col === 0 || col === backRowSeats - 1 });
      }
    }
    return seats;
  }, [totalSeats, leftSeats, rightSeats, backRowSeats, hasBackRow, seatConfig]);

  if (layout.length === 0) {
    return <div style={{ color: '#999', textAlign: 'center', padding: 20 }}>O'rindiqlar yo'q</div>;
  }

  const maxRow = Math.max(...layout.map(s => s.row));
  const maxCol = Math.max(...layout.map(s => s.col));
  const minCol = Math.min(...layout.map(s => s.col));
  const usingSeatConfig = seatConfig && seatConfig.length > 0;

  const totalWidth = usingSeatConfig
    ? (maxCol - minCol + 1) * (seatSize + gap) + 30
    : (leftSeats + rightSeats) * (seatSize + gap) + aisleWidth;
  const totalHeight = (maxRow + 1) * (seatSize + gap) + 70;

  return (
    <div
      style={{
        width: totalWidth,
        height: totalHeight,
        background: '#f5f5f5',
        borderRadius: 12,
        padding: 12,
        position: 'relative',
      }}
    >
      {/* Driver area */}
      <div
        style={{
          position: 'absolute',
          top: 8,
          right: 12,
          width: seatSize,
          height: seatSize,
          background: '#1a1a2e',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CarOutlined style={{ color: '#fff', fontSize: 18 }} />
      </div>

      {/* Seats */}
      <div style={{ marginTop: seatSize + gap + 8 }}>
        {layout.map((seat) => {
          const isBooked = bookedSeats.includes(seat.number);
          const isSelected = selectedSeats.includes(seat.number);
          const isClickable = !isBooked;

          let left: number;
          if (usingSeatConfig) {
            const isAfterAisle = seat.col > 2;
            left = (seat.col - minCol) * (seatSize + gap) + (isAfterAisle ? 20 : 0);
          } else {
            left = seat.col < leftSeats
              ? seat.col * (seatSize + gap)
              : (seat.col - leftSeats) * (seatSize + gap) + leftSeats * (seatSize + gap) + aisleWidth;

            if (seat.row === maxRow && hasBackRow && !usingSeatConfig) {
              const backRowWidth = backRowSeats * (seatSize + gap) - gap;
              const offset = (totalWidth - 2 * 12 - backRowWidth) / 2;
              left = seat.col * (seatSize + gap) + offset - 12;
            }
          }

          let bg = '#00C4AD';
          if (isBooked) bg = '#ff4d4f';
          else if (isSelected) bg = '#1890ff';

          const tooltipText = isBooked
            ? `O'rindiq ${seat.number} (Band)`
            : isSelected
              ? `O'rindiq ${seat.number} (Tanlangan)`
              : `O'rindiq ${seat.number} (Bo'sh)`;

          return (
            <Tooltip key={seat.number} title={tooltipText}>
              <div
                onClick={() => isClickable && onSeatToggle(seat.number)}
                style={{
                  position: 'absolute',
                  left: left + 12,
                  top: seat.row * (seatSize + gap) + seatSize + gap + 8 + 12,
                  width: seatSize,
                  height: seatSize,
                  background: bg,
                  borderRadius: 6,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: isClickable ? 'pointer' : 'not-allowed',
                  border: seat.isWindow ? '2px solid #0099ff' : 'none',
                  boxShadow: isSelected
                    ? '0 0 0 3px rgba(24,144,255,0.3)'
                    : '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s',
                  opacity: isBooked ? 0.7 : 1,
                }}
              >
                {seat.number}
              </div>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

// ============ MAIN COMPONENT ============

export const BusBookingCreate = () => {
  const { show, list } = useNavigation();
  const [searchParams] = useSearchParams();

  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);

  // Step 1: Trip selection
  const [trips, setTrips] = useState<BusTripOption[]>([]);
  const [loadingTrips, setLoadingTrips] = useState(false);
  const [tripSearch, setTripSearch] = useState('');
  const [selectedTrip, setSelectedTrip] = useState<BusTripOption | null>(null);
  const [loadingTripDetails, setLoadingTripDetails] = useState(false);

  // Step 2: Seats & passengers
  const [selectedSeats, setSelectedSeats] = useState<number[]>([]);
  const [passengers, setPassengers] = useState<Passenger[]>([]);

  // Step 3: Confirm
  const [autoConfirmPayment, setAutoConfirmPayment] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Auto-load trip from query params (e.g. from seat map click)
  const preloadTrip = useCallback(async (tripId: number, seatNumber?: number) => {
    setLoadingTripDetails(true);
    try {
      const response = await apiClient.get(`/admin/bus/trips/${tripId}`);
      const tripDetails = response.data.data || response.data;
      setSelectedTrip(tripDetails);
      setCurrentStep(1);

      // Pre-select the seat if provided and not already booked
      if (seatNumber && !tripDetails.bookedSeats?.includes(seatNumber)) {
        setSelectedSeats([seatNumber]);
        setPassengers([{ name: '', phone: '', passport: '', birthDate: '', email: '', seatNumber }]);
      }
    } catch {
      message.error("Reys ma'lumotlarini yuklashda xatolik");
    } finally {
      setLoadingTripDetails(false);
    }
  }, []);

  // Load scheduled trips on mount + handle query params
  useEffect(() => {
    loadTrips();

    const tripId = searchParams.get('tripId');
    const seat = searchParams.get('seat');
    if (tripId) {
      preloadTrip(Number(tripId), seat ? Number(seat) : undefined);
    }
  }, []);

  const loadTrips = async () => {
    setLoadingTrips(true);
    try {
      const response = await apiClient.get('/admin/bus/trips?status=SCHEDULED&limit=100');
      const data = response.data.data || response.data.items || response.data;
      setTrips(Array.isArray(data) ? data : []);
    } catch {
      message.error('Reyslarni yuklashda xatolik');
    } finally {
      setLoadingTrips(false);
    }
  };

  const handleTripSelect = async (trip: BusTripOption) => {
    setLoadingTripDetails(true);
    try {
      const response = await apiClient.get(`/admin/bus/trips/${trip.id}`);
      const tripDetails = response.data.data || response.data;
      setSelectedTrip(tripDetails);
      setSelectedSeats([]);
      setPassengers([]);
      setCurrentStep(1);
    } catch {
      message.error('Reys ma\'lumotlarini yuklashda xatolik');
    } finally {
      setLoadingTripDetails(false);
    }
  };

  const handleSeatToggle = (seatNumber: number) => {
    setSelectedSeats((prev) => {
      const newSeats = prev.includes(seatNumber)
        ? prev.filter((s) => s !== seatNumber)
        : [...prev, seatNumber].sort((a, b) => a - b);

      // Sync passengers with seats
      setPassengers((prevPassengers) => {
        return newSeats.map((seat) => {
          const existing = prevPassengers.find((p) => p.seatNumber === seat);
          if (existing) return existing;
          return { name: '', phone: '', passport: '', birthDate: '', email: '', seatNumber: seat };
        });
      });

      return newSeats;
    });
  };

  const updatePassenger = (seatNumber: number, field: keyof Passenger, value: string) => {
    setPassengers((prev) =>
      prev.map((p) => (p.seatNumber === seatNumber ? { ...p, [field]: value } : p)),
    );
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('uz-UZ').format(value) + " so'm";
  };

  const formatDateTime = (date: string) => {
    return new Date(date).toLocaleString('uz-UZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const totalPrice = selectedTrip ? selectedTrip.pricePerSeat * selectedSeats.length : 0;

  const validateStep1 = (): boolean => {
    if (!selectedTrip) {
      message.warning('Reysni tanlang');
      return false;
    }
    if (selectedSeats.length === 0) {
      message.warning("Kamida bitta o'rindiq tanlang");
      return false;
    }
    for (const p of passengers) {
      if (!p.name.trim()) {
        message.warning(`O'rindiq ${p.seatNumber}: yo'lovchi ismini kiriting`);
        return false;
      }
      if (!/^\+998\d{9}$/.test(p.phone)) {
        message.warning(`O'rindiq ${p.seatNumber}: telefon raqamni to'g'ri kiriting (+998XXXXXXXXX)`);
        return false;
      }
      if (!p.passport.trim()) {
        message.warning(`O'rindiq ${p.seatNumber}: passport raqamini kiriting`);
        return false;
      }
      if (!p.birthDate) {
        message.warning(`O'rindiq ${p.seatNumber}: tug'ilgan sanani kiriting`);
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep === 1) {
      if (!validateStep1()) return;
    }
    setCurrentStep((prev) => prev + 1);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const firstPassenger = passengers[0];
      const payload = {
        busTripId: selectedTrip!.id,
        clientName: firstPassenger.name,
        clientPhone: firstPassenger.phone,
        clientEmail: firstPassenger.email || undefined,
        seatNumbers: selectedSeats,
        passengers: passengers.map((p) => ({
          name: p.name,
          phone: p.phone,
          passport: p.passport,
          birthDate: p.birthDate,
          email: p.email || undefined,
          seatNumber: p.seatNumber,
        })),
        autoConfirmPayment,
      };
      const response = await apiClient.post('/admin/bus/bookings', payload);
      const booking = response.data.data || response.data;
      message.success('Buyurtma muvaffaqiyatli yaratildi');
      show('admin/bus/bookings', booking.id);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Xatolik yuz berdi');
    } finally {
      setSubmitting(false);
    }
  };

  // Filtered trips for search
  const filteredTrips = trips.filter((trip) => {
    if (!tripSearch) return true;
    const q = tripSearch.toLowerCase();
    return (
      trip.departureCity?.nameUz?.toLowerCase().includes(q) ||
      trip.arrivalCity?.nameUz?.toLowerCase().includes(q) ||
      trip.busCompany?.name?.toLowerCase().includes(q)
    );
  });

  const busTypeLabels: Record<string, string> = {
    STANDARD: 'Standart',
    VIP: 'VIP',
    SLEEPER: 'Sleeper',
  };

  // ============ RENDER ============

  return (
    <Create
      isLoading={submitting}
      title="Yangi buyurtma yaratish"
      headerButtons={
        <Button onClick={() => list('admin/bus/bookings')}>Orqaga</Button>
      }
      footerButtons={<></>}
    >
      <Steps
        current={currentStep}
        items={[
          { title: 'Reys tanlash' },
          { title: "O'rindiq va yo'lovchilar" },
          { title: 'Tasdiqlash' },
        ]}
        style={{ marginBottom: 32 }}
      />

      {/* ============ STEP 0: TRIP SELECTION ============ */}
      {currentStep === 0 && (
        <Card
          title="Mavjud reyslar"
          extra={
            <Input
              placeholder="Shahar yoki kompaniya..."
              prefix={<SearchOutlined />}
              value={tripSearch}
              onChange={(e) => setTripSearch(e.target.value)}
              style={{ width: 220 }}
              allowClear
            />
          }
        >
          <Spin spinning={loadingTrips || loadingTripDetails}>
            <Table
              dataSource={filteredTrips}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              size="small"
              locale={{ emptyText: <Empty description="Reyslar topilmadi" /> }}
              onRow={(record) => ({
                onClick: () => handleTripSelect(record),
                style: { cursor: 'pointer' },
              })}
              columns={[
                {
                  title: "Yo'nalish",
                  key: 'route',
                  render: (_: unknown, record: BusTripOption) => (
                    <div>
                      <div style={{ fontWeight: 500 }}>
                        {record.departureCity?.nameUz} → {record.arrivalCity?.nameUz}
                      </div>
                      <div style={{ color: '#7F8C8D', fontSize: 12 }}>
                        {record.departureRegion?.nameUz} → {record.arrivalRegion?.nameUz}
                      </div>
                    </div>
                  ),
                },
                {
                  title: "Jo'nash vaqti",
                  key: 'departure',
                  render: (_: unknown, record: BusTripOption) =>
                    formatDateTime(record.departureTime),
                },
                {
                  title: 'Kompaniya',
                  key: 'company',
                  render: (_: unknown, record: BusTripOption) => record.busCompany?.name,
                },
                {
                  title: "O'rindiqlar",
                  key: 'seats',
                  render: (_: unknown, record: BusTripOption) => (
                    <Tag color={record.availableSeats > 0 ? 'green' : 'red'}>
                      {record.availableSeats}/{record.totalSeats}
                    </Tag>
                  ),
                },
                {
                  title: 'Narx',
                  key: 'price',
                  render: (_: unknown, record: BusTripOption) =>
                    formatCurrency(record.pricePerSeat),
                },
                {
                  title: 'Turi',
                  key: 'type',
                  render: (_: unknown, record: BusTripOption) => (
                    <Tag color={record.busType === 'VIP' ? 'gold' : 'default'}>
                      {busTypeLabels[record.busType] || record.busType}
                    </Tag>
                  ),
                },
              ]}
            />
          </Spin>
        </Card>
      )}

      {/* ============ STEP 1: SEATS & PASSENGERS ============ */}
      {currentStep === 1 && selectedTrip && (
        <>
          {/* Trip summary banner */}
          <Card size="small" style={{ marginBottom: 24, background: '#f6ffed' }}>
            <Space split={<span style={{ color: '#d9d9d9' }}>|</span>}>
              <span style={{ fontWeight: 500 }}>
                {selectedTrip.departureCity?.nameUz} → {selectedTrip.arrivalCity?.nameUz}
              </span>
              <span>{formatDateTime(selectedTrip.departureTime)}</span>
              <span>{selectedTrip.busCompany?.name}</span>
              <span>{formatCurrency(selectedTrip.pricePerSeat)} / o'rindiq</span>
              <Button size="small" onClick={() => { setCurrentStep(0); setSelectedTrip(null); setSelectedSeats([]); setPassengers([]); }}>
                Boshqa reys
              </Button>
            </Space>
          </Card>

          <Row gutter={24}>
            {/* Left: Seat map */}
            <Col xs={24} lg={10}>
              <Card
                title="O'rindiq tanlang"
                extra={
                  selectedTrip.busLayout && (
                    <Tag color="blue">{selectedTrip.busLayout.name}</Tag>
                  )
                }
              >
                <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                  <InteractiveSeatMap
                    totalSeats={selectedTrip.totalSeats}
                    rows={selectedTrip.busLayout?.rows || 10}
                    leftSeats={selectedTrip.busLayout?.leftSeats || 2}
                    rightSeats={selectedTrip.busLayout?.rightSeats || 2}
                    backRowSeats={selectedTrip.busLayout?.backRowSeats || 5}
                    hasBackRow={selectedTrip.busLayout?.hasBackRow ?? true}
                    seatConfig={selectedTrip.busLayout?.seatConfig}
                    bookedSeats={selectedTrip.bookedSeats || []}
                    selectedSeats={selectedSeats}
                    onSeatToggle={handleSeatToggle}
                  />
                </div>
                {/* Legend */}
                <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center', gap: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 20, height: 20, background: '#00C4AD', borderRadius: 4 }} />
                    <span>Bo'sh</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 20, height: 20, background: '#1890ff', borderRadius: 4 }} />
                    <span>Tanlangan</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 20, height: 20, background: '#ff4d4f', borderRadius: 4, opacity: 0.7 }} />
                    <span>Band</span>
                  </div>
                </div>
              </Card>

              {/* Selected seats summary */}
              <Card title="Tanlangan o'rindiqlar" style={{ marginTop: 24 }}>
                {selectedSeats.length === 0 ? (
                  <Empty description="O'rindiq tanlanmagan" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                  <>
                    <Space wrap>
                      {selectedSeats.map((seat) => (
                        <Tag key={seat} color="blue" closable onClose={() => handleSeatToggle(seat)}>
                          O'rindiq {seat}
                        </Tag>
                      ))}
                    </Space>
                    <div style={{ marginTop: 16, padding: '12px 0', borderTop: '1px solid #f0f0f0' }}>
                      <Row justify="space-between">
                        <Col>
                          <span style={{ color: '#7F8C8D' }}>
                            {selectedSeats.length} ta o'rindiq × {formatCurrency(selectedTrip.pricePerSeat)}
                          </span>
                        </Col>
                        <Col>
                          <span style={{ fontSize: 18, fontWeight: 600, color: '#00C4AD' }}>
                            {formatCurrency(totalPrice)}
                          </span>
                        </Col>
                      </Row>
                    </div>
                  </>
                )}
              </Card>
            </Col>

            {/* Right: Passenger details */}
            <Col xs={24} lg={14}>
              {selectedSeats.length === 0 ? (
                <Card>
                  <Empty description="O'rindiq tanlang — yo'lovchi ma'lumotlari shu yerda ko'rsatiladi" />
                </Card>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {passengers.map((passenger, index) => (
                    <Card
                      key={passenger.seatNumber}
                      title={
                        <Space>
                          <Tag color="blue" style={{ fontSize: 13 }}>O'rindiq {passenger.seatNumber}</Tag>
                          {index === 0 && <Tag color="green">Asosiy mijoz</Tag>}
                        </Space>
                      }
                      size="small"
                    >
                      <Row gutter={[12, 12]}>
                        <Col xs={24} sm={12}>
                          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 12 }}>
                            F.I.O <span style={{ color: '#ff4d4f' }}>*</span>
                          </label>
                          <Input
                            prefix={<UserOutlined />}
                            placeholder="To'liq ism"
                            value={passenger.name}
                            onChange={(e) => updatePassenger(passenger.seatNumber, 'name', e.target.value)}
                            status={!passenger.name.trim() ? 'warning' : undefined}
                          />
                        </Col>
                        <Col xs={24} sm={12}>
                          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 12 }}>
                            Telefon <span style={{ color: '#ff4d4f' }}>*</span>
                          </label>
                          <Input
                            prefix={<PhoneOutlined />}
                            placeholder="+998901234567"
                            value={passenger.phone}
                            onChange={(e) => updatePassenger(passenger.seatNumber, 'phone', e.target.value)}
                            status={!/^\+998\d{9}$/.test(passenger.phone) && passenger.phone ? 'warning' : undefined}
                          />
                        </Col>
                        <Col xs={24} sm={12}>
                          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 12 }}>
                            Passport <span style={{ color: '#ff4d4f' }}>*</span>
                          </label>
                          <Input
                            prefix={<IdcardOutlined />}
                            placeholder="AA1234567"
                            value={passenger.passport}
                            onChange={(e) => updatePassenger(passenger.seatNumber, 'passport', e.target.value.toUpperCase())}
                            status={!passenger.passport.trim() ? 'warning' : undefined}
                          />
                        </Col>
                        <Col xs={24} sm={12}>
                          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 12 }}>
                            Tug'ilgan sana <span style={{ color: '#ff4d4f' }}>*</span>
                          </label>
                          <DatePicker
                            placeholder="Sanani tanlang"
                            style={{ width: '100%' }}
                            format="DD.MM.YYYY"
                            value={passenger.birthDate ? dayjs(passenger.birthDate) : null}
                            onChange={(date) => updatePassenger(passenger.seatNumber, 'birthDate', date ? date.format('YYYY-MM-DD') : '')}
                            disabledDate={(current) => current && current > dayjs()}
                            status={!passenger.birthDate ? 'warning' : undefined}
                          />
                        </Col>
                        <Col xs={24}>
                          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 12 }}>
                            Email
                          </label>
                          <Input
                            prefix={<MailOutlined />}
                            placeholder="email@example.com (ixtiyoriy)"
                            value={passenger.email}
                            onChange={(e) => updatePassenger(passenger.seatNumber, 'email', e.target.value)}
                          />
                        </Col>
                      </Row>
                    </Card>
                  ))}
                </div>
              )}
            </Col>
          </Row>
        </>
      )}

      {/* ============ STEP 2: CONFIRMATION ============ */}
      {currentStep === 2 && selectedTrip && (
        <Row gutter={24}>
          <Col xs={24} lg={10}>
            <Card title="Reys ma'lumotlari">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Yo'nalish">
                  {selectedTrip.departureCity?.nameUz} → {selectedTrip.arrivalCity?.nameUz}
                </Descriptions.Item>
                <Descriptions.Item label="Viloyat">
                  {selectedTrip.departureRegion?.nameUz} → {selectedTrip.arrivalRegion?.nameUz}
                </Descriptions.Item>
                <Descriptions.Item label="Jo'nash vaqti">
                  {formatDateTime(selectedTrip.departureTime)}
                </Descriptions.Item>
                <Descriptions.Item label="Yetib borish">
                  {formatDateTime(selectedTrip.arrivalTime)}
                </Descriptions.Item>
                <Descriptions.Item label="Kompaniya">
                  {selectedTrip.busCompany?.name}
                </Descriptions.Item>
                <Descriptions.Item label="Avtobus turi">
                  <Tag color={selectedTrip.busType === 'VIP' ? 'gold' : 'default'}>
                    {busTypeLabels[selectedTrip.busType] || selectedTrip.busType}
                  </Tag>
                </Descriptions.Item>
                {selectedTrip.busNumber && (
                  <Descriptions.Item label="Avtobus raqami">
                    {selectedTrip.busNumber}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            <Card style={{ marginTop: 24 }}>
              <Statistic
                title="Jami summa"
                value={totalPrice}
                formatter={(val) => formatCurrency(Number(val))}
                valueStyle={{ color: '#00C4AD', fontSize: 28 }}
              />
              <div style={{ marginTop: 16, padding: '12px 0', borderTop: '1px solid #f0f0f0' }}>
                <Checkbox
                  checked={autoConfirmPayment}
                  onChange={(e) => setAutoConfirmPayment(e.target.checked)}
                >
                  To'lovni tasdiqlash (darhol to'langan deb belgilash)
                </Checkbox>
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={14}>
            <Card title="Yo'lovchilar">
              <Table
                dataSource={passengers}
                rowKey="seatNumber"
                pagination={false}
                size="small"
                columns={[
                  {
                    title: "O'rindiq",
                    dataIndex: 'seatNumber',
                    key: 'seatNumber',
                    width: 80,
                    render: (seat: number) => <Tag color="blue">{seat}</Tag>,
                  },
                  {
                    title: 'F.I.O',
                    dataIndex: 'name',
                    key: 'name',
                  },
                  {
                    title: 'Telefon',
                    dataIndex: 'phone',
                    key: 'phone',
                  },
                  {
                    title: 'Passport',
                    dataIndex: 'passport',
                    key: 'passport',
                  },
                  {
                    title: "Tug'ilgan sana",
                    dataIndex: 'birthDate',
                    key: 'birthDate',
                    render: (val: string) => val ? dayjs(val).format('DD.MM.YYYY') : '-',
                  },
                  {
                    title: 'Email',
                    dataIndex: 'email',
                    key: 'email',
                    render: (val: string) => val || '-',
                  },
                ]}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* ============ NAVIGATION BUTTONS ============ */}
      <div
        style={{
          marginTop: 32,
          display: 'flex',
          justifyContent: currentStep > 0 ? 'space-between' : 'flex-end',
        }}
      >
        {currentStep > 0 && (
          <Button size="large" onClick={() => setCurrentStep((prev) => prev - 1)}>
            Orqaga
          </Button>
        )}
        {currentStep === 1 && (
          <Button type="primary" size="large" onClick={handleNext}>
            Keyingi
          </Button>
        )}
        {currentStep === 2 && (
          <Button
            type="primary"
            size="large"
            loading={submitting}
            onClick={handleSubmit}
          >
            Buyurtma yaratish
          </Button>
        )}
      </div>
    </Create>
  );
};
